import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useUser } from '@/hooks/useUser';
import { useSnackbar } from '@/features/snackbar';
import { useCompanyNarratives } from '@/pages/CompanyReport/components/CompanyNarratives/hooks/useCompanyNarratives';
import { Narrative } from '@/pages/Narrative/hooks/useNarrativeDeprecated';
import { getQueryKeyDeprecated as getNarrativeQueryKey } from '../useNarrativeDeprecated';
import {
  getQueryKey as getNarrativePermissionQueryKey,
  type PermissionResponse,
} from '../useNarrativePermission';
import { mutationFn } from './mutationFn';
import { updateVoteCounts, updateUserVotedNarratives } from './utils';

export const useVoteOnNarrative = (slug: string, canonicalUrl?: string) => {
  const { data: user } = useUser();
  const queryClient = useQueryClient();
  const { addSnackbar } = useSnackbar();

  return useMutation({
    mutationFn,
    onSuccess: (_, { voteType, narrativeId }) => {
      const userVotedNarratives =
        queryClient.getQueryData<PermissionResponse>(
          getNarrativePermissionQueryKey(user.id)
        )?.userVotedNarratives || [];
      const existingVoteType =
        userVotedNarratives?.find((n) => n.narrativeId === narrativeId)
          ?.voteType || 0;

      if (canonicalUrl) {
        queryClient.setQueryData<Narrative[]>(
          useCompanyNarratives.getOptions({ canonicalUrl }).queryKey,
          (state) => {
            if (typeof state === 'undefined') return state;
            return state.map((narrative) => {
              if (narrative.id === narrativeId) {
                const updatedVoteCount = updateVoteCounts(
                  narrative.voteCount.upvotes,
                  narrative.voteCount.downvotes,
                  existingVoteType,
                  voteType
                );

                return {
                  ...narrative,
                  voteCount: updatedVoteCount,
                };
              }

              return narrative;
            });
          }
        );
      } else {
        queryClient.setQueryData<Narrative>(
          getNarrativeQueryKey(slug),
          (state) => {
            if (typeof state === 'undefined') return state;
            const updatedVoteCount = updateVoteCounts(
              state.voteCount.upvotes,
              state.voteCount.downvotes,
              existingVoteType,
              voteType
            );

            return {
              ...state,
              voteCount: updatedVoteCount,
            };
          }
        );
      }

      queryClient.setQueryData<PermissionResponse>(
        getNarrativePermissionQueryKey(user.id),
        (state) => {
          if (typeof state === 'undefined') return state;
          const updatedUserVotedNarratives = updateUserVotedNarratives(
            state.userVotedNarratives,
            existingVoteType,
            voteType,
            narrativeId
          );
          return {
            ...state,
            userVotedNarratives: updatedUserVotedNarratives,
          };
        }
      );
    },
    onError: (_, { voteType }) => {
      addSnackbar({
        type: 'negative',
        lifeTime: 2000,
        message: `Failed to ${voteType === 1 ? 'up' : 'down'}voted narrative`,
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: getNarrativeQueryKey(slug) });
      queryClient.invalidateQueries({
        queryKey: getNarrativePermissionQueryKey(user.id),
      });
      if (canonicalUrl) {
        queryClient.invalidateQueries(
          useCompanyNarratives.getOptions({ canonicalUrl })
        );
      }
    },
  });
};
